// src/pages/dashboard/DashboardPage.tsx
import { useState, useEffect, useRef, type ReactNode, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

import { WorkflowStatus, ACTIVE_WORKFLOW_STATUSES } from '@/lib/types/workflow';
import type { BookingJob, StockStatus, ReservationLineItem } from '@/lib/types/workflow';
import type { ExtractedGearItem, PricingLineItem } from '@/lib/types/booking';
import { reserveStock, releaseReservation, getReservationByBooking } from '@/lib/services/reservations';
import { mockBookingQueue } from '@/lib/mock/bookings';
import { useBookingQueue, useLiveBookingQueue } from '@/lib/hooks/useBookingQueue';
import { SearchBar } from '@/components/ui/SearchBar';
import type { SearchFilters } from '@/components/ui/SearchBar';
import { claimWorkflow, retryWorkflow, dismissWorkflow, getWorkflow, getEmail, getEmailDrafts, rejectWorkflow, updateDraft, approveWorkflow, finalizeSendWorkflow, revertToPendingApproval, repriceDraft, retryDraftPricing, syncDraftWithReservation, syncDraftWithReservationOnUnload } from '@/lib/services/bookings';

import { brand, fonts, styles, motion } from '@/lib/tokens';
import { statusColors } from '@/lib/statusColors';
import { formatSASTShort } from '@/lib/utils/date';

import { PendingReviewPanel } from './PendingReviewPanel';
import { ActionBar } from '@/components/dashboard/ActionBar';
import { ExtractionProgress } from '@/components/dashboard/ExtractionProgress';
import { StockReservationPanel } from '@/components/dashboard/StockReservationPanel';
import { ClientEmailPanel } from '@/components/dashboard/ClientEmailPanel';
import { AIExtractionPanel } from '@/components/dashboard/AIExtractionPanel';
import { DraftReplyPanel } from '@/components/dashboard/DraftReplyPanel';
import { EditModePanel } from '@/components/dashboard/EditModePanel';
import { FinalizeView } from '@/components/dashboard/FinalizeView';
import { DashboardFooter } from '@/components/dashboard/DashboardFooter';
import { Topbar } from '@/components/layout/Topbar';
import { ThreadBanner } from '@/components/ui/ThreadBanner';
import { Modal } from '@/components/ui/Modal';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { useToast } from '@/context/ToastContext';
import { SkeletonPanel } from '@/components/ui/SkeletonCard';
import { EmptyState } from '@/components/ui/EmptyState';

const LIVE_MODE = true;

type Period = 'all' | 'day' | 'week' | 'month';

const PERIOD_MS: Record<Exclude<Period, 'all'>, number> = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
};

function withinPeriod(isoDate: string | null | undefined, period: Period): boolean {
  if (period === 'all') return true;
  if (!isoDate) return false;
  const ts = new Date(isoDate).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= PERIOD_MS[period];
}

// Actual chronological/alphabetical ordering for InboxQueue's card list —
// distinct from `Period`/`SortBar` above (a recency-window filter, despite
// the name). Display-only: it reorders the rendered EmailCard list but
// never touches `queue` itself, so prev/next navigation (index-based, via
// useBookingQueue) and the queue position shown in ActionBar are unaffected.
type SortOrder = 'date-desc' | 'date-asc' | 'client-asc' | 'client-desc';

const SORT_ORDER_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: 'date-desc', label: 'Newest first' },
  { value: 'date-asc', label: 'Oldest first' },
  { value: 'client-asc', label: 'Client A–Z' },
  { value: 'client-desc', label: 'Client Z–A' },
];

function sortJobsForDisplay(jobs: BookingJob[], order: SortOrder): BookingJob[] {
  const sorted = [...jobs];
  sorted.sort((a, b) => {
    if (order === 'date-desc' || order === 'date-asc') {
      const ta = new Date(a.created_at ?? a.receivedAt ?? 0).getTime();
      const tb = new Date(b.created_at ?? b.receivedAt ?? 0).getTime();
      const na = Number.isNaN(ta) ? 0 : ta;
      const nb = Number.isNaN(tb) ? 0 : tb;
      return order === 'date-desc' ? nb - na : na - nb;
    }
    const ca = (a.clientName ?? '').toLowerCase();
    const cb = (b.clientName ?? '').toLowerCase();
    const cmp = ca.localeCompare(cb);
    return order === 'client-asc' ? cmp : -cmp;
  });
  return sorted;
}

function OrderBar({ order, onChange }: { order: SortOrder; onChange: (o: SortOrder) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ ...styles.monoLabel, fontSize: 11, letterSpacing: '0.08em' }}>Order</span>
      <select
        value={order}
        onChange={(e) => onChange(e.target.value as SortOrder)}
        style={{
          fontFamily: fonts.sans,
          fontSize: 12,
          padding: '4px 10px',
          borderRadius: 4,
          background: 'transparent',
          color: brand.onSurfaceVariant,
          border: `1px solid ${brand.hairlineStrong}`,
        }}
      >
        {SORT_ORDER_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function SortBar({ period, onChange }: { period: Period; onChange: (p: Period) => void }) {
  const options: { value: Exclude<Period, 'all'>; label: string }[] = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
  ];

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      {options.map((opt) => {
        const active = period === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(active ? 'all' : opt.value)}
            style={{
              fontFamily: fonts.sans,
              fontSize: 12,
              padding: '4px 16px',
              borderRadius: 4,
              cursor: 'pointer',
              background: active ? brand.accent : 'transparent',
              color: active ? brand.canvasWarm : brand.textDim,
              border: active ? `1px solid ${brand.accent}` : `1px solid ${brand.hairlineStrong}`,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const PLACEHOLDER_BOOKING: BookingJob = {
  id: '',
  clientName: '',
  clientEmail: '',
  receivedAt: '',
  subject: '',
  emailBody: '',
  status: WorkflowStatus.PROCESSING,
  isThreadReply: false,
  gear: [],
  draftSubject: '',
  draftBody: '',
  aiConfidence: 0,
  location: '',
  rentalDates: '',
  quoteTotalRands: 0,
};

export default function DashboardPage() {
  if (LIVE_MODE) {
    return <LiveDashboardLoader />;
  }
  return <DashboardContent initialQueue={mockBookingQueue} />;
}

function LiveDashboardLoader() {
  const [filters, setFilters] = useState<SearchFilters>({ search: '', date_from: '', date_to: '' });
  const { queue, isLoading, isEnriching, error, refetch, lastCheckedAt, isChecking } = useLiveBookingQueue(filters);

  if (isLoading || isEnriching) {
    return (
      <div style={{ width: '100%', height: '100%', padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 720 }}>
          <SkeletonPanel />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState
          icon="cloud_off"
          title="Something went wrong"
          description="We couldn't load the booking queue right now."
          action={
            <button className="noir-button noir-button-primary" onClick={refetch}>
              Try Again
            </button>
          }
        />
      </div>
    );
  }

  return (
    <DashboardContent
      initialQueue={queue}
      liveQueue={queue}
      onFiltersChange={setFilters}
      lastCheckedAt={lastCheckedAt}
      isChecking={isChecking}
    />
  );
}

function DashboardContent({
  initialQueue,
  liveQueue = [],
  onFiltersChange,
  lastCheckedAt = null,
  isChecking = false,
}: {
  initialQueue: BookingJob[];
  liveQueue?: BookingJob[];
  onFiltersChange?: (f: SearchFilters) => void;
  lastCheckedAt?: number | null;
  isChecking?: boolean;
}) {
  const {
    queue,
    currentBooking,
    queueIndex,
    queueTotal,
    isQueueEmpty,
    workflowStatus,
    setWorkflowStatus,
    reservation,
    setReservation,
    advanceAfterCompletion,
    goToNextBooking,
    goToPrevBooking,
    hasNextBooking,
    hasPrevBooking,
    goToIndex,
    updateJobInQueue,
    selectExternalBooking,
  } = useBookingQueue(initialQueue, liveQueue);

  const [period, setPeriod] = useState<Period>('all');
  const [order, setOrder] = useState<SortOrder>('date-desc');

  const [searchParams, setSearchParams] = useSearchParams();

  // On initial load (or when queue first populates), jump to the booking in
  // the URL. If it isn't in the currently-loaded queue — e.g. it has since
  // moved to a terminal status (SENT/REJECTED/QUARANTINED) and been
  // filtered out of the active queue by useLiveBookingQueue, or the queue's
  // own enrichment sweep just hasn't reached it — fetch it directly instead
  // of silently falling through to the inbound-queue default. That silent
  // fallback was the "hard reload dumps me back on the inbound queue" bug:
  // the booking in the URL was real, we just never looked for it anywhere
  // but the filtered in-memory list.
  const urlHandled = useRef(false);
  useEffect(() => {
    if (urlHandled.current || queue.length === 0) return;
    // Mark handled on this very first run regardless of outcome — this
    // effect exists only to restore a specific booking on a genuine
    // cold-load/hard-reload (URL already had ?booking= before we mounted).
    // If we only set the ref inside the "found a bookingId" branches, a
    // clean mount (login, or navigating in from Archive) would leave it
    // false, and the currentBooking->URL sync effect below — which fires
    // right after and writes queue[0]'s id into the URL — would then look
    // like a fresh URL-driven request to open that booking, yanking the
    // operator off the inbound queue and into whatever the oldest booking
    // happens to be.
    urlHandled.current = true;
    const bookingId = searchParams.get('booking');
    if (!bookingId) return;
    const idx = queue.findIndex((j) => j.id === bookingId);
    if (idx >= 0) {
      goToIndex(idx);
      return;
    }
    _fetchBookingJob(bookingId).then((job) => {
      if (!job) return;
      // Real fix, 28 Sep 2026: this used to set selectedJob/booking/draft/
      // workflowStatus directly and leave currentBookingId untouched — the
      // same "Queue Complete" desync bug as handleSelectJob's fallback just
      // below (see that function's own comment and
      // useBookingQueue.selectExternalBooking's comment for the full
      // history). Routing through selectExternalBooking keeps
      // currentBookingId, isQueueEmpty, queueIndex and this component's own
      // booking-sync effect all in agreement with what's on screen.
      setSelectedJob(job);
      selectExternalBooking(job);
    });
  }, [queue, searchParams, goToIndex, selectExternalBooking]);

  // Keep URL in sync with the current booking — but only once the operator
  // has actually opened one. `currentBooking` defaults to queue[0] the
  // instant the queue loads, whether or not anyone has selected it; writing
  // it into the URL while still on the inbound queue (PROCESSING) planted a
  // ?booking=<oldest-booking-id> param on every fresh dashboard load, which
  // a later reload would then read back as "restore this booking" and open
  // it instead of the queue.
  useEffect(() => {
    if (workflowStatus === WorkflowStatus.PROCESSING) {
      // Back on the queue (e.g. "Back to Queue", or Archive -> Dashboard nav)
      // — clear any stale booking id so a later reload lands on the queue
      // too, instead of re-opening whatever was last viewed.
      setSearchParams((prev) => {
        if (!prev.has('booking')) return prev;
        const next = new URLSearchParams(prev);
        next.delete('booking');
        return next;
      }, { replace: true });
      return;
    }
    if (!currentBooking?.id) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('booking', currentBooking.id);
      return next;
    }, { replace: true });
  }, [currentBooking?.id, workflowStatus, setSearchParams]);

  const { user } = useAuth();

  const toast = useToast();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [datesStart, setDatesStart] = useState('');
  const [datesEnd, setDatesEnd] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [selectedJob, setSelectedJob] = useState<BookingJob | null>(null);

  // Last-known-good version of whichever job is selected from the queue, so
  // PendingReviewPanel never regresses from real data back to blank
  // 'Loading...' placeholders. `queue.find()` below is a fresh lookup into
  // whatever useLiveBookingQueue's 30s poll currently has — every poll cycle
  // rebuilds that array, and if this booking's enrichment patch hasn't
  // landed back into it yet at the moment of a re-render, the lookup can
  // momentarily return a bare placeholder object for an id we already had
  // full data for. `booking` (below) has the equivalent guard via
  // syncedBookingId; this mirrors it for the selected-job path, which
  // PendingReviewPanel uses instead of `booking` for the queue-click flow.
  // See the "title and columns flash to Loading..." report, 26 Aug 2026.
  const lastGoodSelectedJob = useRef<BookingJob | null>(null);
  //
  // Real fix, 28 Oct 2026: `selectedJob` is only ever set by an explicit
  // queue click (handleSelectJob) or a ?booking= restore. The prev/next
  // arrows (and advanceAfterCompletion) move useBookingQueue's
  // currentBookingId, which drives the header, ?booking= URL and `booking`,
  // but never touched `selectedJob` — so after one queue click, every arrow
  // press changed the header/booking id while PendingReviewPanel kept
  // rendering the first email (confirmed live: three different booking ids
  // and header names over one unchanged email/extraction/draft). A selected
  // job that no longer matches the current booking is stale: drop it so the
  // panels follow currentBooking.
  const liveSelectedJob = (() => {
    if (!selectedJob || (currentBooking && selectedJob.id !== currentBooking.id)) {
      lastGoodSelectedJob.current = null;
      return null;
    }
    const found = queue.find((j) => j.id === selectedJob.id) ?? selectedJob;
    const isBlankPlaceholder =
      found.clientName === 'Loading...' && found.emailBody === '' && found.gear.length === 0;
    const haveGoodDataForSameJob =
      lastGoodSelectedJob.current?.id === found.id &&
      !(lastGoodSelectedJob.current.clientName === 'Loading...' && lastGoodSelectedJob.current.emailBody === '');
    if (isBlankPlaceholder && haveGoodDataForSameJob) {
      return lastGoodSelectedJob.current!;
    }
    lastGoodSelectedJob.current = found;
    return found;
  })();

  function handleSelectJob(job: BookingJob) {
    setSelectedJob(job);
    // Real fix, 22 Sep 2026: this used to ONLY set local selectedJob/
    // workflowStatus state, leaving useBookingQueue's currentBookingId (and
    // everything derived from it — queueIndex, isQueueEmpty, currentBooking,
    // and the ?booking= URL-sync effect further up this component) pointing
    // at whatever it already was. Nothing else in this click path (EmailCard's
    // handleClick, for a card already past RECEIVED) ever calls goToIndex/
    // advanceQueue/resetQueue either, so currentBookingId could still be null
    // from initial mount (its default is initialQueue[0]?.id, which is null
    // whenever the dashboard first mounted with an empty/different queue).
    // With isQueueEmpty defined as currentBookingId === null, that produced a
    // nonsensical "Queue Complete · N+1/N" screen (queueIndex falling back to
    // queue.length) immediately after clicking a queue card whose
    // backendStatus was already past RECEIVED — until a hard reload ran the
    // ?booking= URL-restore effect, which does call goToIndex and fixes it.
    // Reported 22 Sep 2026 as "still have to reload to see the booking" — a
    // distinct bug from (and not fixed by) the syncedBookingId
    // over-suppression fix earlier the same day, since PendingReviewPanel's
    // underlying data (liveSelectedJob) was already correct all along; it was
    // just being hidden behind this spurious Queue Complete screen.
    //
    // goToIndex() already derives workflowStatus from the queue's own current
    // copy of this booking via statusForJob (same logic previously duplicated
    // below), and also resets `reservation` — correct for a freshly-selected
    // booking. Fall back to selectExternalBooking() in the edge case where
    // `job` isn't found in `queue` (e.g. fetched via _fetchBookingJob for a
    // ?booking= id the live queue hasn't picked up yet) — this used to fall
    // back to only setting workflowStatus directly, leaving currentBookingId
    // untouched and reproducing the same "Queue Complete" desync this whole
    // function's fix was originally meant to close. Fixed 28 Sep 2026 — see
    // selectExternalBooking's comment in useBookingQueue.ts for the full
    // history.
    const idx = queue.findIndex((j) => j.id === job.id);
    if (idx !== -1) {
      goToIndex(idx);
      return;
    }
    selectExternalBooking(job);
  }

  function handleClaimComplete(enrichedJob: BookingJob) {
    // Real fix, 23 Sep 2026: this function was the actual target of the
    // 22 Sep "claim-complete booking-id desync" fix (PR #108) — the
    // useBookingQueue hook picked up `updateJobInQueue`/`goToIndex` support
    // specifically for this call site (see that helper's own comment), but
    // this function itself was never actually wired to call either one. It
    // kept only setting local `booking`/`draft`/`workflowStatus` state
    // directly, leaving `currentBookingId` at whatever it already was
    // (`null` on a fresh session, since the hook's default is
    // `initialQueue[0]?.id ?? null`) — reproducing the exact "Queue
    // Complete" flash on the very first claim of a session that PR #108's
    // own description claimed to have closed. Confirmed live 23 Sep 2026:
    // claiming the first booking of a fresh Injozi session still flashed
    // "Queue Complete · 2/1" despite PR #108 being deployed.
    //
    // Same pattern as handleSelectJob's fix: patch the freshly-enriched job
    // into the hook's own `queue` copy first (so the booking-sync effect's
    // next render doesn't clobber it with a stale pre-claim snapshot from
    // the live queue's last poll), then route through goToIndex so
    // `currentBookingId`/`queueIndex`/`isQueueEmpty` all move together with
    // what's actually on screen.
    //
    // 21 Oct 2026 — the remaining cause of "claimed booking stays on the
    // inbox instead of opening the 3-panel view": goToIndex derives the
    // screen from the queued job's backendStatus, which is still
    // RECEIVED/PROCESSING in the stale `queue` of this render (and
    // _enrichJob never updates backendStatus), so the status resolved back
    // to PROCESSING = InboxQueue. The claim poll only calls this once the
    // backend reports PENDING_APPROVAL, so stamp that status on the job and
    // select it directly.
    const claimed: BookingJob = { ...enrichedJob, backendStatus: 'PENDING_APPROVAL' };
    updateJobInQueue(claimed);
    setBooking(claimed);
    setDraft(claimed.draftBody);
    setSelectedJob(null);
    selectExternalBooking(claimed);
  }

  // Claim from the 3-panel view (28 Oct 2026). The prev/next arrows open any
  // booking, including a RECEIVED (unclaimed) one; ActionBar then offers a
  // Claim & Extract button instead of Edit/Reject/Approve. Mirrors EmailCard's
  // claim flow (claimWorkflow -> 3s poll of getWorkflow until PENDING_APPROVAL
  // -> _enrichJob) but each claim keeps running in the background, so the
  // operator can click Claim & Extract and keep arrowing through the inbox:
  // several extractions can be in flight at once. When one finishes, it opens
  // in place if the operator is still on it; otherwise it is patched into the
  // queue quietly (with a toast) and never yanks them away from what they're
  // reading. Extraction is deliberately operator-triggered, not automatic on
  // arrival: every claim is a real Claude call and assigns the booking.
  const [claimingIds, setClaimingIds] = useState<string[]>([]);
  const claimPollsRef = useRef<Map<string, number>>(new Map());
  // When this session started each claim -- feeds the elapsed timer in the
  // extraction progress cue. Absent for bookings already PROCESSING on load.
  const claimStartedAtRef = useRef<Map<string, number>>(new Map());
  const handleClaimCompleteRef = useRef(handleClaimComplete);
  handleClaimCompleteRef.current = handleClaimComplete;
  const currentIdRef = useRef<string | null>(null);
  currentIdRef.current = currentBooking?.id ?? null;

  function finishClaimTracking(id: string) {
    const handle = claimPollsRef.current.get(id);
    if (handle !== undefined) clearInterval(handle);
    claimPollsRef.current.delete(id);
    claimStartedAtRef.current.delete(id);
    setClaimingIds((prev) => prev.filter((x) => x !== id));
  }

  // Stop every outstanding poll on unmount (the backend claims still finish).
  useEffect(() => {
    const polls = claimPollsRef.current;
    return () => {
      polls.forEach((handle) => clearInterval(handle));
      polls.clear();
    };
  }, []);

  async function handleClaimFromReview() {
    const target = booking;
    if (target.backendStatus !== 'RECEIVED' || claimPollsRef.current.has(target.id)) return;
    claimStartedAtRef.current.set(target.id, Date.now());
    setClaimingIds((prev) => [...prev, target.id]);
    try {
      await claimWorkflow(target.id);
    } catch (err) {
      claimStartedAtRef.current.delete(target.id);
      setClaimingIds((prev) => prev.filter((x) => x !== target.id));
      const message =
        err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : '';
      if (message.toLowerCase().includes('already claimed')) {
        toast.warning(message);
      } else {
        toast.error('Could not claim this booking - please try again');
      }
      return;
    }

    let elapsed = 0;
    const handle = window.setInterval(async () => {
      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= POLL_TIMEOUT_MS) {
        finishClaimTracking(target.id);
        toast.error(`AI extraction for ${target.clientName} is taking longer than expected - check the inbox`);
        return;
      }
      try {
        const workflow = (await getWorkflow(target.id)) as Record<string, unknown>;
        // Tracking already finished (unmount, or a duplicate tick) - ignore.
        if (!claimPollsRef.current.has(target.id)) return;
        const wfStatus = workflow.status as string | undefined;
        if (wfStatus === 'PENDING_APPROVAL') {
          finishClaimTracking(target.id);
          const enriched = await _enrichJob(target, workflow.raw_email_id as string | undefined);
          if (currentIdRef.current === target.id) {
            handleClaimCompleteRef.current(enriched);
          } else {
            updateJobInQueue({ ...enriched, backendStatus: 'PENDING_APPROVAL' });
            toast.success(`Extraction ready: ${enriched.clientName}`);
          }
        } else if (wfStatus === 'FAILED') {
          finishClaimTracking(target.id);
          toast.error(`AI extraction failed for ${target.clientName} - retry it from the inbox`);
        }
      } catch {
        // transient fetch error - keep polling
      }
    }, POLL_INTERVAL_MS);
    claimPollsRef.current.set(target.id, handle);
  }

  // Local mutable copy of the booking (gear edits + draft edits).
  const [booking, setBooking] = useState<BookingJob>(initialQueue[0] ?? PLACEHOLDER_BOOKING);
  const [draft, setDraft] = useState(initialQueue[0]?.draftBody ?? '');

  // Sync local mutable copy when the queue advances to a new booking.
  //
  // Guarded on booking id + workflowStatus, not just currentBooking's object
  // identity. useLiveBookingQueue's 30s background poll rebuilds `queue`
  // with brand-new object references every cycle — even when nothing about
  // the operator's currently-open booking actually changed. This effect
  // used to re-run on every one of those cycles regardless of what screen
  // the operator was on, silently overwriting whatever they'd typed into
  // the draft or edited in EDIT_MODE with whatever the background poll
  // happened to hand back — see the "new email comes in and yanks me out of
  // what I'm editing" report, 25 Aug 2026.
  //
  // Real fix, 22 Sep 2026: the same-booking suppression above was applied to
  // EVERY non-PROCESSING screen, including PENDING_REVIEW and
  // STOCK_RESERVATION — neither of which lets the operator type into the
  // draft or edit gear (PendingGrid's DraftReplyPanel isn't `editable`; the
  // stock panel has no local booking-field edits at all). Because the
  // suppression is permanent for a given booking id (nothing ever resets
  // syncedBookingId short of navigating to a different booking), landing on
  // PENDING_REVIEW before AI extraction/draft data had fully finished
  // writing — or before a correction landed — meant the panels stayed on
  // that first snapshot forever: the operator had to hard-reload the page
  // (which remounts everything from scratch) to see the extraction actually
  // update, reported live 22 Sep 2026 ("the extraction doesn't show
  // immediately as it changes until i hit reload page"). The original
  // 25 Aug 2026 bug only ever needed protecting the screens where a local
  // edit genuinely exists to lose — EDIT_MODE (gear qty/price/swap, draft
  // text) and FINALIZE (FinalizeView's editable draft textarea) — so narrow
  // the suppression to just those, letting PENDING_REVIEW/STOCK_RESERVATION
  // (both read-only w.r.t. booking data) always pick up the latest polled
  // data for the same booking id.
  const syncedBookingId = useRef<string | null>(null);
  useEffect(() => {
    if (!currentBooking) return;
    const sameBooking = syncedBookingId.current === currentBooking.id;
    const isEditableScreen =
      workflowStatus === WorkflowStatus.EDIT_MODE || workflowStatus === WorkflowStatus.FINALIZE;
    if (sameBooking && isEditableScreen) return;
    syncedBookingId.current = currentBooking.id;
    setBooking(currentBooking);
    setDraft(currentBooking.draftBody);
  }, [currentBooking, workflowStatus]);

  // If we land on STOCK_RESERVATION (the workflow is already APPROVED on the
  // backend — see toFrontendStatus) with no reservation loaded locally, e.g.
  // the operator navigated back into an already-approved booking or a reload
  // lost component state, first check whether a reservation already exists
  // for this booking and reuse it. Only fall through to creating a fresh one
  // when none exists yet.
  //
  // Real fix, 16 Sep 2026: this effect used to call reserveStock()
  // unconditionally whenever it found no reservation in local component
  // state — which is true on every mount, including a plain page reload or
  // navigating back into an already-approved booking. That silently created
  // a brand-new StockReservation row (and re-ran the live Current RMS
  // availability check) on every such visit, with no operator action
  // involved at all — directly contradicting the "manual" stock-hold
  // architecture (reservation creation is supposed to happen exactly once,
  // as a side effect of an explicit Approve). See
  // claude/finding-dashboard-auto-recreates-stock-reservation-on-reload.md.
  // Checking getReservationByBooking() first makes this idempotent: an
  // existing reservation is reloaded and reused, never duplicated.
  // Holds the booking id a reservation load/create is currently running for
  // (null when idle) — an id, not a boolean, so a load started for one booking
  // never blocks the load for the booking the operator has since moved to.
  const creatingReservation = useRef<string | null>(null);
  // Tracks which booking id we've already tried a backfill fetch for, so a
  // booking that genuinely has no dates only shows the error toast once —
  // not on every re-render while sitting on this screen.
  const backfillAttemptedFor = useRef<string | null>(null);
  useEffect(() => {
    if (workflowStatus !== WorkflowStatus.STOCK_RESERVATION) return;
    if (reservation !== null) return;
    // 30 Oct 2026: `booking` is a local copy that catches up to the queue's
    // current booking one render AFTER the arrows (or a deep link) move to
    // another email — see the sync effect above. For that one render
    // workflowStatus already belongs to the NEW booking while `booking` is
    // still the OLD one, so this effect looked up (and failed to find dates
    // for) the wrong booking and flashed "No rental dates found on this
    // booking" even though the right one loaded fine a moment later. Wait
    // until the local copy is the booking actually on screen.
    if (currentBooking && booking.id !== currentBooking.id) return;
    if (creatingReservation.current === booking.id) return;

    const loadingFor = booking.id;
    creatingReservation.current = loadingFor;
    // Anything the operator navigated away from mid-request is dropped
    // silently — no toast, no state written for a booking no longer shown.
    const stillCurrent = () => currentIdRef.current === null || currentIdRef.current === loadingFor;

    (async () => {
      try {
        const existing = await getReservationByBooking(loadingFor);
        if (!stillCurrent()) return;
        if (existing) {
          setReservation(existing);
          return;
        }

        if (!booking.rentalDateRange) {
          // useLiveBookingQueue's enrichment sweep sometimes hasn't attached
          // extracted_dates to this booking by the time we land here (a
          // fresh page load/reload racing the sweep, or a thread-reply
          // booking whose draft is keyed to a different raw_email_id than
          // this one). Re-fetch this booking's draft directly once before
          // giving up — this is what used to just toast "No rental dates
          // found" and dead-end.
          if (backfillAttemptedFor.current === booking.id) {
            toast.error('No rental dates found on this booking — cannot check stock');
            return;
          }
          backfillAttemptedFor.current = booking.id;
          const fetched = await _fetchBookingJob(booking.id);
          if (!stillCurrent()) return;
          if (fetched?.rentalDateRange) {
            setBooking((b) => (b.id === fetched.id ? { ...b, ...fetched } : b));
          } else {
            toast.error('No rental dates found on this booking — cannot check stock');
          }
          return;
        }

        const items = booking.gear.map((g) => ({
          gearItemId: g.id,
          name: g.name,
          quantity: g.quantity,
          unitPrice: g.unitPrice,
        }));
        const created = await reserveStock(
          booking.id,
          items,
          booking.rentalDateRange.start,
          booking.rentalDateRange.end,
        );
        if (!stillCurrent()) return;
        setReservation(created);
      } catch (err: unknown) {
        if (!stillCurrent()) return;
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to load stock reservation';
        toast.error(msg);
      } finally {
        if (creatingReservation.current === loadingFor) creatingReservation.current = null;
      }
    })();
    // `toast` deliberately excluded — ToastContext.tsx builds a brand-new
    // `value` object on every ToastProvider render rather than memoizing it,
    // so it has a new identity almost every render; including it here would
    // re-run this effect (and its reservation-creation logic) far more often
    // than intended. The creatingReservation/reservation-not-null guards
    // above make an extra re-run harmless regardless, but there's no reason
    // to invite it. Fixing ToastContext's own memoization is a separate,
    // wider cleanup (AuthContext/ThemeContext/ToastContext all share this
    // pattern) — out of scope for this change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowStatus, reservation, booking, currentBooking?.id, setReservation]);

  // ---- Edit handlers
  function onQtyChange(id: string, qty: number) {
    setBooking((b) => ({
      ...b,
      gear: b.gear.map((g) => (g.id === id ? { ...g, quantity: qty } : g)),
    }));
  }
  function onUnitPriceChange(id: string, price: number) {
    setBooking((b) => ({
      ...b,
      gear: b.gear.map((g) => (g.id === id ? { ...g, unitPrice: price } : g)),
    }));
  }
  function onRemove(id: string) {
    setBooking((b) => ({ ...b, gear: b.gear.filter((g) => g.id !== id) }));
  }
  function onSwap(id: string) {
    // Stub — Phase 4 opens the stock picker. Mark substitution.
    setBooking((b) => ({
      ...b,
      gear: b.gear.map((g) =>
        g.id === id
          ? { ...g, stockStatus: 'SUBSTITUTION', substitutionNote: 'Operator-selected alternative.' }
          : g,
      ),
    }));
  }

  // Operator-confirmed CPT/CPT-Local switch (see
  // finding-cpt-jhb-tier-pricing-doc-gap.md, 24 Aug 2026) — only ever
  // reachable for a Cape Town booking, only ever operator-triggered.
  // Re-prices and regenerates the draft server-side, then replaces the
  // local booking/draft state with the authoritative result.
  const [isRepricing, setIsRepricing] = useState(false);
  async function handleReprice(tier: 'CPT' | 'CPT Local') {
    if (!booking.draftId) return;
    setIsRepricing(true);
    try {
      const repriced = await repriceDraft(booking.draftId, tier);
      const pricingLines = repriced.pricing_line_items ?? [];
      setBooking((b) => ({
        ...b,
        gear: b.gear.map((g, idx) => ({
          ...g,
          unitPrice: pricingLines[idx]?.day_rate ?? g.unitPrice,
        })),
        quoteTotalRands: repriced.total_price ?? b.quoteTotalRands,
        priceTier: repriced.price_tier,
        draftSubject: repriced.subject,
        draftBody: repriced.body_plain,
      }));
      setDraft(repriced.body_plain);
      toast.success(`Switched to ${tier} pricing`);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to re-price draft';
      toast.error(msg);
    } finally {
      setIsRepricing(false);
    }
  }

  // After a substitution decision on the Stock Reservation screen (an
  // accepted AI suggestion, or an operator-entered manual substitute — see
  // inventory-service's [[finding-manual-substitute-entry]]), re-narrate
  // the draft so its body/pricing/gear list reflect what's actually being
  // held rather than what was originally requested. Not called for a
  // declined substitute — nothing about the quoted item actually changed
  // there, it just stays CONFLICT/unresolved. See
  // [[finding-finalize-draft-edit-discarded-on-send]] for the related bug
  // this closes the loop on.
  //
  // Draft re-sync queue (14 Oct 2026): each sync is a full Claude re-draft, so
  // it takes several seconds. Fired fire-and-forget per decision, rapid
  // decisions raced each other (an older response could land last and win),
  // and nothing stopped the operator confirming the hold — and sending —
  // while the regenerated draft was still being written. Now: decisions are
  // debounced into one sync of the latest full line list, syncs never overlap
  // (a decision made mid-sync is picked up by a follow-up sync once the
  // current one finishes), a response is only applied if nothing newer is
  // queued and the operator is still on the same booking, and
  // draftSyncStatus gates Confirm Manual Hold in StockReservationPanel until
  // the draft is saved (with Retry on failure).
  const [draftSyncStatus, setDraftSyncStatus] = useState<'idle' | 'syncing' | 'failed'>('idle');
  type DraftSyncJob = {
    bookingId: string;
    draftId: string;
    lines: ReservationLineItem[];
    dates?: { start: string; end: string };
  };
  const draftSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftSyncInFlight = useRef(false);
  const draftSyncPending = useRef<DraftSyncJob | null>(null);
  const draftSyncFailedJob = useRef<DraftSyncJob | null>(null);
  const activeBookingIdRef = useRef(booking.id);
  activeBookingIdRef.current = booking.id;

  // A different booking starts with a clean slate; a sync still running for
  // the previous one only reports back for its own booking (see the guards
  // in flushDraftSync).
  useEffect(() => {
    setDraftSyncStatus('idle');
    draftSyncFailedJob.current = null;
  }, [booking.id]);
  useEffect(() => () => {
    if (draftSyncTimer.current) clearTimeout(draftSyncTimer.current);
  }, []);

  async function flushDraftSync(): Promise<void> {
    draftSyncTimer.current = null;
    if (draftSyncInFlight.current) return; // the running sync re-flushes when it finishes
    const job = draftSyncPending.current;
    if (!job) return;
    draftSyncPending.current = null;
    draftSyncInFlight.current = true;
    let failureMessage: string | null = null;
    try {
      const synced = await syncDraftWithReservation(
        job.draftId,
        job.lines.map((l) => ({ name: l.name, quantity: l.quantity, unitPrice: l.unitPrice })),
        job.dates,
      );
      // A newer decision is already queued (its sync will overwrite this) or
      // the operator has moved to another booking — don't apply a stale result.
      if (draftSyncPending.current === null && activeBookingIdRef.current === job.bookingId) {
        const pricingLines = synced.pricing_line_items ?? [];
        setBooking((b) => ({
          ...b,
          gear: job.lines.map((l, idx) => ({
            id: l.gearItemId,
            name: l.name,
            quantity: l.quantity,
            unitPrice: pricingLines[idx]?.day_rate ?? l.unitPrice,
            stockStatus: (l.reservationStatus === 'SUBSTITUTED' ? 'SUBSTITUTION' : 'IN_STOCK') as StockStatus,
          })),
          quoteTotalRands: synced.total_price ?? b.quoteTotalRands,
          draftSubject: synced.subject,
          draftBody: synced.body_plain,
        }));
        setDraft(synced.body_plain);
      }
    } catch (err: unknown) {
      failureMessage =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to update the draft with the resolved stock';
      draftSyncFailedJob.current = job;
    } finally {
      draftSyncInFlight.current = false;
    }
    if (draftSyncPending.current) {
      // A decision arrived mid-sync — run again with the latest lines.
      draftSyncFailedJob.current = null;
      await flushDraftSync();
      return;
    }
    if (activeBookingIdRef.current !== job.bookingId) return;
    if (failureMessage) {
      toast.error(failureMessage);
      setDraftSyncStatus('failed');
    } else {
      setDraftSyncStatus('idle');
    }
  }

  function queueDraftSync(lines: ReservationLineItem[]) {
    if (!booking.draftId) return;
    draftSyncPending.current = { bookingId: booking.id, draftId: booking.draftId, lines, dates: booking.rentalDateRange };
    draftSyncFailedJob.current = null;
    setDraftSyncStatus('syncing');
    if (draftSyncTimer.current) clearTimeout(draftSyncTimer.current);
    draftSyncTimer.current = setTimeout(() => {
      void flushDraftSync();
    }, 1000);
  }

  function handleReservationLinesChanged(updatedLines: ReservationLineItem[]) {
    setReservation((r) => (r ? { ...r, lines: updatedLines } : r));
    queueDraftSync(updatedLines);
  }

  // Unload safety: a sync still waiting out its debounce when the tab is
  // closed / navigated away would be lost. Send it right then with keepalive
  // (the server runs it to completion regardless of the client going away),
  // and ask the browser to confirm the close while a sync is pending or
  // running. Reads refs only, so it is registered once.
  useEffect(() => {
    function onPageHide() {
      const job = draftSyncPending.current;
      if (!job || draftSyncInFlight.current) return; // in-flight syncs finish server-side on their own
      if (draftSyncTimer.current) clearTimeout(draftSyncTimer.current);
      draftSyncTimer.current = null;
      draftSyncPending.current = null;
      syncDraftWithReservationOnUnload(
        job.draftId,
        job.lines.map((l) => ({ name: l.name, quantity: l.quantity, unitPrice: l.unitPrice })),
        job.dates,
      );
    }
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (draftSyncPending.current || draftSyncInFlight.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  // Drift check: a sync can still be lost (browser crash, network drop, tab
  // closed mid-request). So when the stock screen opens for a reservation
  // that has substituted lines, compare what the reservation holds with what
  // the draft was last built from (name + quantity, order-independent) and,
  // if they differ, run the same gated sync automatically. Once per
  // reservation per mount, so a failed sync can't retry in a loop (the
  // operator gets the Retry link instead).
  const driftCheckedFor = useRef<string | null>(null);
  useEffect(() => {
    if (workflowStatus !== WorkflowStatus.STOCK_RESERVATION || !reservation || !booking.draftId) return;
    if (booking.gear.length === 0) return; // draft data not loaded yet — check again once it is
    if (driftCheckedFor.current === reservation.id) return;
    driftCheckedFor.current = reservation.id;
    if (!reservation.lines.some((l) => l.reservationStatus === 'SUBSTITUTED')) return;
    const key = (name: string, quantity: number) => `${name}|${quantity}`;
    const held = reservation.lines.map((l) => key(l.name, l.quantity)).sort().join('\n');
    const drafted = booking.gear.map((g) => key(g.name, g.quantity)).sort().join('\n');
    if (held !== drafted) queueDraftSync(reservation.lines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowStatus, reservation, booking.draftId, booking.gear]);

  function retryDraftSync() {
    const failed = draftSyncFailedJob.current;
    if (!failed) return;
    draftSyncFailedJob.current = null;
    draftSyncPending.current = failed;
    setDraftSyncStatus('syncing');
    void flushDraftSync();
  }

  // ---- State machine
  function goEdit() { setWorkflowStatus(WorkflowStatus.EDIT_MODE); }
  function goCancelEdit() { setWorkflowStatus(WorkflowStatus.PENDING_REVIEW); }
  // Only for the StockReservationPanel -> FinalizeView handoff, once the
  // operator has already been through approveAndSend() (workflow is
  // APPROVED on the backend and stock holds are confirmed) — see
  // onAllHeld below. Do NOT wire this to any entry point that can be
  // reached while the workflow is still PENDING_APPROVAL.
  function goFinalize() {
    setWorkflowStatus(WorkflowStatus.FINALIZE);
  }

  // Always available from ActionBar now (see ActionBar.tsx, 17 Sep 2026) —
  // an operator must be able to bail back to the inbound queue from any
  // screen (mid-edit, mid-stock-check, or on Finalize), not just
  // PENDING_REVIEW. Clears local reservation/selectedJob state so a later
  // revisit re-derives it fresh (getReservationByBooking is idempotent —
  // see the STOCK_RESERVATION-mount effect above) instead of resuming a
  // possibly-stale in-memory object. Never releases an in-progress
  // reservation itself — that's still only "Cancel Reservation" in
  // StockReservationPanel, since leaving the screen isn't the same as the
  // operator deciding to abandon the hold.
  function goBackToQueue() {
    setSelectedJob(null);
    setReservation(null);
    setWorkflowStatus(WorkflowStatus.PROCESSING);
  }
  function goReject() { setRejectOpen(true); }
  function goApprove() { setApproveOpen(true); }

  // "Save & Finalize" from EDIT_MODE used to call goFinalize() directly,
  // which only ever set local UI state — it never called approveWorkflow()
  // or reserveStock(), so the operator could land on the Finalize & Send
  // screen for a workflow the backend still considered PENDING_APPROVAL,
  // and "Finalize & Send" would then correctly reject it. This persists
  // the draft edit first (goFinalize()'s old behavior), then routes
  // through the same approve + stock-check flow as the "Approve" button
  // so the operator only ever reaches FinalizeView once the workflow is
  // genuinely APPROVED and stock has been confirmed.
  async function saveAndApprove() {
    if (booking.draftId) {
      await updateDraft(booking.draftId, { subject: booking.draftSubject, body_plain: draft });
    }
    await approveAndSend();
  }

  // Real fix, 29 Sep 2026: this used to catch-and-ignore any rejectWorkflow
  // failure and advance the queue regardless ("optimistic"). That made a
  // real backend failure — most concretely, a 422 from reject_workflow()
  // guarding status == PENDING_APPROVAL when the workflow was actually
  // still APPROVED (see goCancelReservation's fix above for how that
  // happened) — completely invisible: the operator would see the normal
  // "Confirm Rejection" -> "Queue Complete" flow with no error, while the
  // booking was never actually transitioned to REJECTED server-side and so
  // never showed up in Archive -> Rejected. Reported live as "the fix only
  // worked for one email". Now a failure surfaces a toast and does NOT
  // advance the queue, so the operator stays on the booking and can retry
  // (or report a real, visible error) instead of the dashboard silently
  // lying about what happened. See
  // [[finding-cancel-reservation-does-not-revert-workflow-status]].
  async function confirmReject() {
    setRejectOpen(false);
    try {
      await rejectWorkflow(booking.id, { actor_id: user?.email ?? 'operator', reason: rejectReason });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to reject — the booking has not been moved to Rejected';
      toast.error(msg);
      return;
    }
    setRejectReason('');
    advanceAfterCompletion(booking.id);
  }

  async function confirmApprove() {
    setApproveOpen(false);
    await approveAndSend();
  }

  async function confirmDates() {
    if (!datesStart || !datesEnd) {
      toast.error('Enter both a start and an end date');
      return;
    }
    if (datesEnd < datesStart) {
      toast.error('The end date cannot be before the start date');
      return;
    }
    setDatesOpen(false);
    await approveAndSend({ start: datesStart, end: datesEnd });
  }

  // Finding 1/4: this used to call approveWorkflow() and immediately treat
  // the booking as sent. It now only marks the workflow APPROVED (the
  // backend no longer auto-sends off that transition — see workflow-
  // service's approve_workflow()) and routes the operator into the real
  // STOCK_RESERVATION screen. Sending only happens from finalizeAndSend()
  // below, after the operator confirms holds/substitutions there.
  async function approveAndSend(dateOverride?: { start: string; end: string }) {
    if (!booking.draftId) {
      toast.error('No draft available to approve');
      return;
    }
    setIsApproving(true);
    try {
      // Safety fix, 22 Oct 2026: the "no dates -> skip the stock check and
      // send directly" branch below used to trust `booking.rentalDateRange`
      // from the local cache. That cache is rewritten by the live-queue
      // booking-sync effect every 30s poll, and a freshly rebuilt queue entry
      // has no extracted_dates until its enrichment sweep lands - so an
      // Approve clicked in that window took the bypass branch and SENT the
      // email without the Stock Check screen. Never decide "this booking has
      // no dates" from the cache: confirm against the backend first, and
      // fail closed (send nothing, approve nothing) if that can't be verified.
      // Done before approveWorkflow() so a failure here leaves the workflow
      // untouched at PENDING_APPROVAL.
      let effective = booking;
      if (dateOverride) {
        // Operator typed the dates in because the email had none (see the
        // "Rental dates needed" modal) - use them for the stock check.
        effective = { ...booking, rentalDateRange: dateOverride, rentalDates: `${dateOverride.start} - ${dateOverride.end}` };
        setBooking(effective);
      } else if (!booking.rentalDateRange) {
        const wf =(await getWorkflow(booking.id)) as unknown as Record<string, unknown>;
        const rawEmailId = wf.raw_email_id as string | undefined;
        const drafts = rawEmailId ? await getEmailDrafts(rawEmailId) : [];
        const latest = drafts.find((d) => d.id === booking.draftId) ?? drafts[0];
        const verifiedRange = _rentalDateRange(latest?.extracted_dates ?? []);
        if (verifiedRange) {
          const fresh = await _enrichJob(booking, rawEmailId);
          effective = {
            ...booking,
            rentalDateRange: verifiedRange,
            rentalDates: fresh.rentalDates,
            gear: fresh.gear.length > 0 ? fresh.gear : booking.gear,
          };
          setBooking(effective);
        }
      }

      // Oct 2026 (client demo feedback): a booking with no rental dates used
      // to skip the Stock Check screen and SEND a follow-up email straight
      // away. Alexis/Thato expect every booking to go through the same
      // journey, so ask the operator for the dates here instead - before
      // approveWorkflow() runs, so cancelling the dialog leaves the workflow
      // untouched at PENDING_APPROVAL and nothing is sent.
      if (!effective.rentalDateRange) {
        setDatesOpen(true);
        return;
      }

      // A booking's local backendStatus can be stale in *either* direction
      // when the operator reaches this screen — e.g. they approved it in an
      // earlier session and the frontend's local workflowStatus never got
      // resynced (a separate gap — see the queue-navigation status-sync
      // issue). We used to pre-check `booking.backendStatus !== 'APPROVED'`
      // before deciding whether to call approveWorkflow() at all — but that
      // pre-check reads from the same locally-cached `booking` object the
      // staleness gap is about, so it can be wrong in both directions:
      //   - stale cache still shows PENDING_APPROVAL while the backend is
      //     already APPROVED -> the guard calls approveWorkflow() again ->
      //     422 "cannot be approved from status APPROVED" (reported live by
      //     an operator stuck on this screen with no way to retry, 26 Aug
      //     2026).
      //   - stale cache shows APPROVED (or anything != PENDING_APPROVAL)
      //     while the backend is still PENDING_APPROVAL -> the guard SKIPS
      //     approveWorkflow() entirely -> reserveStock()/
      //     finalizeSendWorkflow() then 422s with "Workflow is
      //     PENDING_APPROVAL, not APPROVED" (reported 07 Sep 2026 — same
      //     root cause, opposite direction).
      // Rather than let local state decide whether to call the endpoint at
      // all, always attempt approveWorkflow() and treat the specific
      // "already approved" 422 as confirmation the approve step is already
      // done, continuing to reserveStock() instead of aborting — any other
      // error still propagates to the outer catch. This removes the
      // dependency on the stale local cache in both directions instead of
      // patching one direction at a time.
      try {
        await approveWorkflow(booking.id, { actor_id: user?.id ?? 'unknown', reason: '' });
      } catch (approveErr: unknown) {
        const approveMsg =
          approveErr && typeof approveErr === 'object' && 'message' in approveErr
            ? String((approveErr as { message: unknown }).message)
            : '';
        if (approveMsg.includes('Retry pricing') && booking.draftId) {
          // N3: the backend refused to approve a quote whose pricing failed
          // (or is inconsistent). Recover in place: re-run pricing once,
          // show the operator the re-priced quote, and let them approve
          // again — never approve/send an unpriced quote.
          await recoverPricing(booking.draftId, approveMsg);
          return;
        }
        if (!approveMsg.includes('cannot be approved from status APPROVED')) {
          throw approveErr;
        }
      }
      // Defensive: the dates check above guarantees a range by this point.
      if (!effective.rentalDateRange) return;
      const items = effective.gear.map((g) => ({
        gearItemId: g.id,
        name: g.name,
        quantity: g.quantity,
        unitPrice: g.unitPrice,
      }));
      const created = await reserveStock(
        booking.id,
        items,
        effective.rentalDateRange.start,
        effective.rentalDateRange.end,
      );
      setReservation(created);
      setWorkflowStatus(WorkflowStatus.STOCK_RESERVATION);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to approve workflow';
      toast.error(msg);
    } finally {
      setIsApproving(false);
    }
  }

  // Real fix, 29 Sep 2026: this used to only reset LOCAL screen state
  // (reservation/workflowStatus) — it never told workflow-service anything
  // happened. That left the backend workflow stuck at APPROVED after a
  // cancel, which caused two live-reported bugs: (1) the dashboard queue
  // kept showing the "Approved" badge for a booking the operator had just
  // backed out of, since the queue's badge comes from the backend's own
  // status, not this screen's local state; (2) a subsequent Reject attempt
  // 422'd, because reject_workflow() guards status == PENDING_APPROVAL and
  // the backend was still APPROVED. Fixed by actually calling the backend
  // transition (APPROVED -> PENDING_APPROVAL, added to workflow-service's
  // VALID_TRANSITIONS specifically for this) and patching the queue's own
  // copy of the job immediately via updateJobInQueue, rather than waiting
  // on the next 30s live-queue poll. See
  // [[finding-cancel-reservation-does-not-revert-workflow-status]].
  //
  // Real fix #2, 29 Sep 2026: the block below used to run
  // setWorkflowStatus(PENDING_REVIEW) UNCONDITIONALLY, even when the
  // revertToPendingApproval() call above had just thrown and been caught.
  // That meant a failed backend revert (e.g. two rapid "Cancel Reservation"
  // clicks firing this function twice, or a transient network error) still
  // flipped the operator's screen to a fully-interactive "Pending Review"
  // view with working-looking Reject/Approve buttons — while the backend
  // workflow was still actually stuck at APPROVED. Clicking Reject from
  // there 422'd, and confirmReject() below used to swallow that silently
  // and advance the queue anyway, so the operator saw a "successful"
  // reject/"Queue Complete" that never happened server-side — reported
  // live as "the fix only worked for one email". Fixed by only moving the
  // screen to PENDING_REVIEW when the backend call actually succeeded, and
  // otherwise leaving the operator on the stock-check screen (where
  // "Cancel Reservation" is still available to retry) with the error
  // visible. See [[finding-cancel-reservation-does-not-revert-workflow-status]].
  async function goCancelReservation() {
    if (reservation) {
      try {
        await releaseReservation(reservation.id);
      } catch {
        // best-effort — still proceed even if the inventory-service release fails
      }
    }
    try {
      await revertToPendingApproval(booking.id, { actor_id: user?.id ?? 'unknown', reason: '' });
      updateJobInQueue({ ...booking, backendStatus: 'PENDING_APPROVAL' });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to revert the workflow to Pending Review — please try Cancel Reservation again';
      toast.error(msg);
      // Do NOT advance to PENDING_REVIEW here — the backend workflow is
      // still APPROVED, so a Reject from that screen would 422 and, before
      // this fix, would have been silently swallowed by confirmReject().
      return;
    }
    setReservation(null);
    setWorkflowStatus(WorkflowStatus.PENDING_REVIEW);
  }

  // N3 (28 Oct 2026): pricing recovery. The first (automatic) pricing attempt
  // can fail - inventory-service unreachable, the priced-draft Claude call
  // failing, etc. - leaving a draft with no total. The backend now refuses to
  // approve or send such a draft; this retries the pricing step once and
  // swaps the re-priced draft into the screen so the operator can review it.
  async function recoverPricing(draftId: string, originalMessage: string) {
    toast.info('This quote has no valid pricing - retrying the pricing step...');
    try {
      const repriced = await retryDraftPricing(draftId);
      const pricingLines = repriced.pricing_line_items ?? [];
      setBooking((b) => ({
        ...b,
        gear: b.gear.map((g, idx) => ({ ...g, unitPrice: pricingLines[idx]?.day_rate ?? g.unitPrice })),
        quoteTotalRands: repriced.total_price ?? b.quoteTotalRands,
        draftSubject: repriced.subject,
        draftBody: repriced.body_plain,
      }));
      setDraft(repriced.body_plain);
      toast.success('Pricing applied. Please review the updated quote, then approve again.');
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : '';
      toast.error(detail || originalMessage);
    }
  }

  // N3: finalize-send only QUEUES the send (the email goes out in a backend
  // background task). Wait for the workflow to actually reach SENT before
  // telling the operator it was sent.
  async function waitForSent(workflowId: string): Promise<boolean> {
    const deadline = Date.now() + 45000;
    while (Date.now() < deadline) {
      try {
        const wf = await getWorkflow(workflowId);
        if (String((wf as unknown as { status?: string }).status) === 'SENT') return true;
      } catch {
        // transient - keep polling until the deadline
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    return false;
  }

  const finalizingRef = useRef(false);

  // Rebuild the quote from the booking's current stock reservation (every
  // line, including substitutions) and swap the result into the screen.
  async function resyncQuoteFromReservation(draftId: string) {
    try {
      const current = await getReservationByBooking(booking.id);
      if (!current) {
        toast.error('Could not load the stock reservation to update the quote');
        return;
      }
      const synced = await syncDraftWithReservation(
        draftId,
        current.lines.map((l) => ({ name: l.name, quantity: l.quantity, unitPrice: l.unitPrice })),
        booking.rentalDateRange,
      );
      const pricingLines = synced.pricing_line_items ?? [];
      setReservation(current);
      setBooking((b) => ({
        ...b,
        gear: current.lines.map((l, idx) => ({
          id: l.gearItemId,
          name: l.name,
          quantity: l.quantity,
          unitPrice: pricingLines[idx]?.day_rate ?? l.unitPrice,
          stockStatus: (l.reservationStatus === 'SUBSTITUTED' ? 'SUBSTITUTION' : 'IN_STOCK') as StockStatus,
        })),
        quoteTotalRands: synced.total_price ?? b.quoteTotalRands,
        draftSubject: synced.subject,
        draftBody: synced.body_plain,
      }));
      setDraft(synced.body_plain);
      toast.warning('Your stock substitutions were missing from the quote and have now been added. Please review it, then send again.');
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : '';
      toast.error(detail || 'Could not update the quote with your stock substitutions');
    }
  }

  // Real send, gated on the operator having confirmed the stock hold in
  // StockReservationPanel first (Finding 1). Calls the new
  // finalize-send endpoint rather than faking a SENT transition locally.
  //
  // Persists the draft edit first — FinalizeView's textarea is editable
  // and shows a "Saved"/"Auto-saving…" indicator, but that indicator is
  // purely local UI state (a setTimeout in FinalizeView.tsx), not a real
  // round trip. Before this fix, finalizeAndSend() went straight to
  // finalizeSendWorkflow() without ever calling updateDraft(), so any
  // edit made on this screen was silently discarded on send while the UI
  // told the operator it had been saved. saveAndApprove() (the EDIT_MODE
  // path) already does this same persist-before-transition; this mirrors
  // it. Reported live 16 Sep 2026 — see
  // [[finding-finalize-draft-edit-discarded-on-send]].
  async function finalizeAndSend() {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    try {
      if (booking.draftId) {
        await updateDraft(booking.draftId, { subject: booking.draftSubject, body_plain: draft });
      }
      await finalizeSendWorkflow(booking.id, { actor_id: user?.id ?? 'unknown', reason: '' });
      toast.info(`Sending quote to ${booking.clientName}...`);
      const sentId = booking.id;
      const confirmed = await waitForSent(sentId);
      if (!confirmed) {
        toast.warning(
          'The quote has not been confirmed as sent. Check the Audit Log - a "Send failed" entry shows the reason - before trying again.',
        );
        return;
      }
      setWorkflowStatus(WorkflowStatus.SENT);
      toast.success(`Quote sent to ${booking.clientName}`);
      setTimeout(() => advanceAfterCompletion(sentId), 1200);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to send quote';
      // Server-side guard (N3): a Stock Check substitution is missing from
      // the quote. Rebuild the quote from the reservation right now and let
      // the operator review it, instead of leaving them stuck on an error.
      if (msg.includes('not reflected in the quote') && booking.draftId) {
        await resyncQuoteFromReservation(booking.draftId);
      } else {
        toast.error(msg);
      }
    } finally {
      finalizingRef.current = false;
    }
  }

  function renderContent(): ReactNode {
    // isQueueEmpty (queueIndex >= queue.length) is true both when the
    // operator has paged past the last item in an active review session
    // AND when the queue is simply empty from the start (0 bookings) — two
    // different things. The "Queue Complete" screen below is only right for
    // the former. When we're still on the inbound queue view (PROCESSING),
    // always render InboxQueue and let its own "No inbound emails" empty
    // state handle a genuinely empty queue, instead of the congratulatory
    // "processed all bookings" screen showing on a fresh/cleared queue.
    if (isQueueEmpty && workflowStatus !== WorkflowStatus.PROCESSING) {
      return (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '24px',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontFamily: fonts.serif,
                fontSize: '48px',
                color: brand.surfaceDim,
                marginBottom: '16px',
                lineHeight: 1,
              }}
            >
              ✦
            </div>
            <h2
              style={{
                fontFamily: fonts.serif,
                fontSize: '28px',
                fontWeight: 400,
                color: brand.onSurface,
                margin: '0 0 8px',
              }}
            >
              Queue Complete
            </h2>
            <p
              style={{
                fontFamily: fonts.mono,
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: brand.monoDim,
                margin: 0,
              }}
            >
              {queueTotal} booking{queueTotal !== 1 ? 's' : ''} processed · All clear
            </p>
          </div>
          <a
            href="/archive"
            style={{
              border: `1px solid ${brand.hairlineMid}`,
              background: 'transparent',
              color: brand.onSurfaceVariant,
              padding: '10px 20px',
              borderRadius: '8px',
              fontFamily: fonts.sans,
              fontSize: '13px',
              textDecoration: 'none',
            }}
          >
            View Archive
          </a>
        </div>
      );
    }

    switch (workflowStatus) {
      case WorkflowStatus.PROCESSING:
        return (
          <InboxQueue
            queue={queue}
            onSelect={handleSelectJob}
            onClaimComplete={handleClaimComplete}
            period={period}
            order={order}
            lastCheckedAt={lastCheckedAt}
            isChecking={isChecking}
          />
        );
      case WorkflowStatus.PENDING_REVIEW:
        if (liveSelectedJob) {
          return (
            <PendingReviewPanel
              job={liveSelectedJob}
              onBack={() => {
                setSelectedJob(null);
                setWorkflowStatus(WorkflowStatus.PROCESSING);
              }}
            />
          );
        }
        return (
          <PendingGrid
            booking={booking}
            draft={draft}
            onDraftChange={setDraft}
            onReprice={handleReprice}
            isRepricing={isRepricing}
            extracting={booking.backendStatus === 'PROCESSING' || claimingIds.includes(booking.id)}
            extractionStartedAt={claimStartedAtRef.current.get(booking.id)}
          />
        );
      case WorkflowStatus.EDIT_MODE:
        return (
          <EditGrid
            booking={booking}
            draft={draft}
            onDraftChange={setDraft}
            onQtyChange={onQtyChange}
            onUnitPriceChange={onUnitPriceChange}
            onSwap={onSwap}
            onRemove={onRemove}
          />
        );
      case WorkflowStatus.STOCK_RESERVATION:
        return reservation === null ? (
          <TerminalPlaceholder label="Preparing Stock Reservation" />
        ) : (
          <StockReservationPanel
            reservation={reservation}
            bookingId={booking.id}
            actorId={user?.id ?? 'unknown'}
            onSubstitutionConfirmed={handleReservationLinesChanged}
            draftSyncStatus={draftSyncStatus}
            onRetryDraftSync={retryDraftSync}
            onAllHeld={goFinalize}
            onCancel={goCancelReservation}
          />
        );
      case WorkflowStatus.FINALIZE:
        return <FinalizeView booking={booking} draft={draft} onDraftChange={setDraft} />;
      case WorkflowStatus.SENT:
        return <TerminalPlaceholder label="Quote Sent" />;
      case WorkflowStatus.REJECTED:
        return <TerminalPlaceholder label="Quote Rejected" />;
      case WorkflowStatus.APPROVED:
        return <TerminalPlaceholder label="Approved" />;
      default:
        return null;
    }
  }

  return (
    <>
      <Topbar />

      <div style={{
        padding: '8px 20px',
        borderBottom: '1px solid rgba(var(--fg-rgb),0.05)',
        background: 'var(--input-recessed-bg)',
      }}>
        <SearchBar
          onSearch={(f) => onFiltersChange?.(f)}
          placeholder="Search by client email or subject..."
        />
        {workflowStatus === WorkflowStatus.PROCESSING && (
          <div style={{ marginTop: 10 }}>
            <OrderBar order={order} onChange={setOrder} />
          </div>
        )}
      </div>

      <ActionBar
        booking={booking}
        state={workflowStatus}
        queueIndex={queueIndex}
        queueTotal={queueTotal}
        onPrev={goToPrevBooking}
        onNext={goToNextBooking}
        canGoPrev={hasPrevBooking}
        canGoNext={hasNextBooking}
        onClaim={handleClaimFromReview}
        isClaiming={claimingIds.includes(booking.id)}
        onEdit={goEdit}
        onReject={goReject}
        onApproveAndSend={goApprove}
        isApproving={isApproving}
        onCancelEdit={goCancelEdit}
        onSaveAndFinalize={saveAndApprove}
        onDiscard={() => setWorkflowStatus(WorkflowStatus.PENDING_REVIEW)}
        onSaveDraft={() => setWorkflowStatus(WorkflowStatus.PENDING_REVIEW)}
        onSend={finalizeAndSend}
        onBackToQueue={goBackToQueue}
      />


      {!isQueueEmpty &&
        booking.isThreadReply &&
        booking.threadRootId &&
        workflowStatus !== WorkflowStatus.PROCESSING && (
          <div style={{ flexShrink: 0 }}>
            <ThreadBanner threadRootId={booking.threadRootId} />
          </div>
        )}

      <div
        className="dashboard-content-area"
        style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <ErrorBoundary>{renderContent()}</ErrorBoundary>
      </div>

      <DashboardFooter />

      <Modal
        open={rejectOpen}
        title="Confirm Rejection"
        body={
          <>
            Booking <span className="font-mono">{booking.id}</span> will be rejected and moved
            to the Deleted tab in Archive. This cannot be undone.
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)"
              style={{
                background: brand.surfaceSubtle,
                border: `1px solid ${brand.hairlineMid}`,
                color: brand.onSurface,
                width: '100%',
                marginTop: 12,
                padding: 10,
                borderRadius: 8,
                fontFamily: fonts.sans,
              }}
            />
          </>
        }
        confirmLabel="Reject"
        confirmVariant="danger"
        onCancel={() => setRejectOpen(false)}
        onConfirm={confirmReject}
      />

      <Modal
        open={approveOpen}
        title="Confirm Approval"
        body="This will approve the quote and check live stock for each item. You'll confirm the stock hold and review the quote before anything is sent to the client."
        confirmLabel="Approve & Check Stock"
        confirmVariant="primary"
        onCancel={() => setApproveOpen(false)}
        onConfirm={confirmApprove}
      />

      <Modal
        open={datesOpen}
        title="Rental dates needed"
        body={
          <>
            The client's email doesn't include rental dates, and stock can't be checked without them. Enter the dates
            to continue to the Stock Check - nothing is sent until you confirm the hold.
            <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
              {[
                { label: 'Start', value: datesStart, set: setDatesStart },
                { label: 'End', value: datesEnd, set: setDatesEnd },
              ].map((f) => (
                <label key={f.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontFamily: fonts.mono, fontSize: 11, textTransform: 'uppercase' }}>{f.label}</span>
                  <input
                    type="date"
                    value={f.value}
                    onChange={(e) => f.set(e.target.value)}
                    style={{
                      background: brand.surfaceSubtle,
                      border: `1px solid ${brand.hairlineMid}`,
                      color: brand.onSurface,
                      padding: 10,
                      borderRadius: 8,
                      fontFamily: fonts.sans,
                    }}
                  />
                </label>
              ))}
            </div>
          </>
        }
        confirmLabel="Continue to Stock Check"
        confirmVariant="primary"
        onCancel={() => setDatesOpen(false)}
        onConfirm={confirmDates}
      />
    </>
  );
}

// ----------------------------------------------------------------------------
// Layout helpers — one per workflow state.
// ----------------------------------------------------------------------------

function PendingGrid({
  booking,
  draft,
  onDraftChange,
  onReprice,
  isRepricing,
  extracting,
  extractionStartedAt,
}: {
  booking: BookingJob;
  draft: string;
  onDraftChange: (s: string) => void;
  onReprice?: (tier: 'CPT' | 'CPT Local') => void | Promise<void>;
  isRepricing?: boolean;
  extracting?: boolean;
  extractionStartedAt?: number;
}) {
  // From claim until PENDING_APPROVAL the extraction and draft panels have
  // nothing real to show. Keep the client email visible (it is already
  // known) and replace the two empty panels with one progress cue.
  if (extracting) {
    return (
      <div className="workflow-grid">
        <ClientEmailPanel booking={booking} />
        <ExtractionProgress startedAt={extractionStartedAt} clientName={booking.clientName} />
      </div>
    );
  }
  return (
    <div className="workflow-grid">
      <ClientEmailPanel booking={booking} />
      <AIExtractionPanel booking={booking} />
      <DraftReplyPanel
        booking={booking}
        draft={draft}
        onDraftChange={onDraftChange}
        onReprice={onReprice}
        isRepricing={isRepricing}
      />
    </div>
  );
}

function EditGrid({
  booking,
  draft,
  onDraftChange,
  onQtyChange,
  onUnitPriceChange,
  onSwap,
  onRemove,
}: {
  booking: BookingJob;
  draft: string;
  onDraftChange: (s: string) => void;
  onQtyChange: (id: string, qty: number) => void;
  onUnitPriceChange: (id: string, price: number) => void;
  onSwap: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="workflow-grid">
      <ClientEmailPanel booking={booking} />
      <EditModePanel
        booking={booking}
        onQtyChange={onQtyChange}
        onUnitPriceChange={onUnitPriceChange}
        onSwap={onSwap}
        onRemove={onRemove}
      />
      <DraftReplyPanel
        booking={booking}
        draft={draft}
        onDraftChange={onDraftChange}
        editable
        showToolbar
      />
    </div>
  );
}

// ── Helpers for enriching a job after claim ──────────────────────────────────

function _parseDisplayName(senderEmail: string): string {
  const match = senderEmail.match(/^(.+?)\s*<[^>]+>$/);
  return match ? match[1].trim() : senderEmail;
}

function _parseEmailAddress(senderEmail: string): string {
  const match = senderEmail.match(/<([^>]+)>/);
  return match ? match[1] : senderEmail;
}

function _formatRentalDates(dates: string[]): string {
  if (!dates.length) return '';
  const sorted = [...dates].sort();
  const days = sorted.length;
  const toDate = (s: string) => new Date(s + 'T00:00:00Z');
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  if (days === 1) return `${fmt(toDate(sorted[0]))} (1 day)`;
  return `${fmt(toDate(sorted[0]))} - ${fmt(toDate(sorted[sorted.length - 1]))} (${days} days)`;
}

/** Real stock status from inventory-service's check_availability() (via the
 *  pricing line's is_available/substitute_available) — mirrors the same
 *  helper in useBookingQueue.ts. Before 26 Aug 2026 every matched gear item
 *  was hardcoded to 'IN_STOCK' here regardless of true quantity on hand. */
function _deriveStockStatus(line: PricingLineItem | undefined): StockStatus {
  if (!line) return 'IN_STOCK';
  if (!line.matched) return 'UNAVAILABLE';
  if (line.is_available) return 'IN_STOCK';
  return line.substitute_available ? 'SUBSTITUTION' : 'UNAVAILABLE';
}

/** Raw ISO start/end behind _formatRentalDates' display string — reserveStock()
 *  needs real dates, not the formatted label. */
function _rentalDateRange(dates: string[]): { start: string; end: string } | undefined {
  if (!dates.length) return undefined;
  const sorted = [...dates].sort();
  return { start: sorted[0], end: sorted[sorted.length - 1] };
}

async function _enrichJob(job: BookingJob, rawEmailId: string | undefined): Promise<BookingJob> {
  if (!rawEmailId) return { ...job, status: WorkflowStatus.PENDING_REVIEW };

  const [emailResult, draftsResult] = await Promise.allSettled([
    getEmail(rawEmailId),
    getEmailDrafts(rawEmailId),
  ]);

  const emailUpdates: Partial<BookingJob> =
    emailResult.status === 'fulfilled'
      ? {
          clientName: _parseDisplayName(emailResult.value.sender_email),
          clientEmail: _parseEmailAddress(emailResult.value.sender_email),
          receivedAt: emailResult.value.received_at ?? '',
          subject: emailResult.value.subject,
          emailBody: emailResult.value.body_plain ?? '',
        }
      : {};

  const draftUpdates: Partial<BookingJob> =
    draftsResult.status === 'fulfilled' && draftsResult.value.length > 0
      ? (() => {
          const draft = draftsResult.value[0];
          // pricing_line_items is index-matched to extracted_gear — see the
          // matching comment in useBookingQueue.ts's own enrichment path.
          const pricingLines = draft.pricing_line_items ?? [];
          return {
            draftId: draft.id,
            aiConfidence: Math.round((draft.ai_confidence_score ?? 0) * 100),
            gear: (draft.extracted_gear ?? []).map((g: ExtractedGearItem, idx: number) => ({
              // Positional, slash-free id — see useBookingQueue.ts (the id is a
              // URL path segment for reservation line calls; names can contain "/").
              id: `gear-${idx}`,
              name: g.name,
              quantity: g.quantity,
              unitPrice: pricingLines[idx]?.day_rate ?? 0,
              stockStatus: _deriveStockStatus(pricingLines[idx]),
            })),
            location: draft.extracted_location ?? '',
            rentalDates: _formatRentalDates(draft.extracted_dates ?? []),
            rentalDateRange: _rentalDateRange(draft.extracted_dates ?? []),
            draftBody: draft.body_plain ?? '',
            draftSubject: draft.subject ?? '',
            quoteTotalRands: draft.total_price ?? 0,
            priceTier: draft.price_tier ?? null,
          };
        })()
      : {};

  return { ...job, ...emailUpdates, ...draftUpdates, status: WorkflowStatus.PENDING_REVIEW };
}

/**
 * Fetches and enriches a single booking directly by ID, independent of the
 * (filtered, sometimes-stale) live queue list. Used to restore a specific
 * booking from the "?booking=" URL param when it isn't present in `queue`
 * — e.g. it's since moved to a terminal status (SENT/REJECTED/QUARANTINED)
 * and been filtered out of the active queue, or the live queue's own
 * enrichment sweep hasn't reached it yet — and to backfill rental dates
 * for the stock-reservation auto-create effect above. Returns null on any
 * fetch failure (e.g. the workflow no longer exists) so callers can fall
 * back gracefully rather than throwing.
 */
async function _fetchBookingJob(bookingId: string): Promise<BookingJob | null> {
  try {
    const raw = await getWorkflow(bookingId);
    const w = raw as unknown as Record<string, unknown>;
    const base: BookingJob = {
      ...PLACEHOLDER_BOOKING,
      id: bookingId,
      backendStatus: w.status as string | undefined,
      created_at: w.created_at as string | undefined,
      assignedToEmail: w.assigned_to_email as string | null | undefined,
    };
    return await _enrichJob(base, w.raw_email_id as string | undefined);
  } catch {
    return null;
  }
}

interface InboxQueueProps {
  queue: BookingJob[];
  onSelect: (job: BookingJob) => void;
  onClaimComplete: (enrichedJob: BookingJob) => void;
  period: Period;
  order: SortOrder;
  lastCheckedAt?: number | null;
  isChecking?: boolean;
}

// Sourced from the same shared ACTIVE_WORKFLOW_STATUSES constant
// bookings.ts's getActiveWorkflows() now fetches from — this used to be
// its own independently-hardcoded list that already included DRAFTED/
// APPROVED/RESERVATION_CONFIRMED/FAILED (with badges for each, below)
// while the fetch-side list in bookings.ts lagged behind at just
// RECEIVED/PROCESSING/PENDING_APPROVAL, so those cards had nothing to
// render — see ACTIVE_WORKFLOW_STATUSES's doc comment in workflow.ts.
const INBOX_QUEUE_STATUSES = new Set<string>(ACTIVE_WORKFLOW_STATUSES);

function InboxQueue({
  queue,
  onSelect,
  onClaimComplete,
  period,
  order,
  lastCheckedAt = null,
  isChecking = false,
}: InboxQueueProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const items = sortJobsForDisplay(
    queue
      .filter((j) => !!j.backendStatus && INBOX_QUEUE_STATUSES.has(j.backendStatus))
      .filter((j) => !dismissed.has(j.id))
      .filter((j) => withinPeriod(j.created_at, period)),
    order,
  );

  function handleDismiss(id: string) {
    setDismissed((prev) => new Set([...prev, id]));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          borderBottom: `1px solid ${brand.hairlineSoft}`,
          background: brand.canvas,
        }}
      >
        <span style={{ ...styles.monoLabel, color: brand.accent }}>INBOUND QUEUE</span>
        <span style={styles.monoLabel}>{items.length} AWAITING AI EXTRACTION</span>
      </div>
      <MailCheckCue lastCheckedAt={lastCheckedAt} isChecking={isChecking} />

      {items.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontFamily: fonts.serif,
              fontSize: 32,
              color: brand.monoDarker,
              lineHeight: 1,
            }}
          >
            ✦
          </span>
          <span
            style={{
              fontFamily: fonts.sans,
              fontSize: 18,
              color: brand.monoDim,
              marginTop: 12,
            }}
          >
            No inbound emails
          </span>
          <span
            style={{
              ...styles.monoLabel,
              color: brand.monoDarker,
              marginTop: 6,
            }}
          >
            Waiting for incoming client requests
          </span>
        </div>
      ) : (
        <div
          className="custom-scroll"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {items.map((job) => (
            <EmailCard
              key={job.id}
              job={job}
              onSelect={onSelect}
              onClaimComplete={onClaimComplete}
              onDismiss={() => handleDismiss(job.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** "Checking for new mail" cue. New emails reach the inbox on two 30 s
 *  cycles (mail intake, then this dashboard's refresh), so one can take up to
 *  about a minute to show up. Say so, and show the poll is alive. */
function MailCheckCue({ lastCheckedAt, isChecking }: { lastCheckedAt: number | null; isChecking: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, []);

  const secondsAgo = lastCheckedAt === null ? null : Math.max(0, Math.floor((now - lastCheckedAt) / 1000));
  const label = isChecking
    ? 'Checking for new mail...'
    : secondsAgo === null
      ? 'Waiting for first check...'
      : `Inbox checked ${secondsAgo < 5 ? 'just now' : `${secondsAgo}s ago`}`;

  return (
    <div
      role="status"
      aria-live="off"
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 24px',
        borderBottom: `1px solid ${brand.hairlineSoft}`,
        background: brand.canvas,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: brand.accent,
          animation: isChecking ? 'dotPulse 0.8s ease-in-out infinite' : undefined,
          opacity: isChecking ? undefined : 0.5,
        }}
      />
      <span style={{ ...styles.monoLabel, color: brand.monoDim }}>
        {label} {' · '} New emails can take up to a minute to appear
      </span>
    </div>
  );
}

function getInboxBadge(backendStatus?: string): { label: string } {
  switch (backendStatus) {
    case 'DRAFTED':
      return { label: 'DRAFTING REPLY' };
    case 'PENDING_APPROVAL':
      return { label: 'PENDING REVIEW' };
    case 'APPROVED':
      return { label: 'APPROVED' };
    case 'RESERVATION_CONFIRMED':
      return { label: 'CONFIRMING RESERVATION' };
    default:
      return { label: 'INBOUND' };
  }
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 180000;
const STALL_THRESHOLD_MS = 3 * 60 * 1000;

function EmailCard({
  job,
  onSelect,
  onClaimComplete,
  onDismiss,
}: {
  job: BookingJob;
  onSelect: (job: BookingJob) => void;
  onClaimComplete: (enrichedJob: BookingJob) => void;
  onDismiss: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const toast = useToast();
  const { user } = useAuth();

  // If created_at is more than STALL_THRESHOLD_MS old and still PROCESSING, show stalled immediately.
  const isAlreadyStalled =
    job.backendStatus === 'PROCESSING' &&
    !!job.created_at &&
    Date.now() - new Date(job.created_at).getTime() > STALL_THRESHOLD_MS;

  const [claimState, setClaimState] = useState<'idle' | 'processing' | 'failed' | 'timeout' | 'stalled'>(
    () => {
      if (job.backendStatus === 'FAILED') return 'failed';
      if (isAlreadyStalled) return 'stalled';
      if (job.backendStatus === 'PROCESSING') return 'processing';
      return 'idle';
    },
  );
  const intervalRef = useRef<number | null>(null);
  // When this operator started the claim (elapsed timer in the progress cue).
  // Undefined for a card that was already PROCESSING when the page loaded.
  const claimStartedAtRef = useRef<number | undefined>(undefined);

  // Re-sync claimState whenever the backend's own status for this job changes.
  // The lazy useState initializer above only runs once, on mount — with
  // key={job.id} keeping the same EmailCard instance alive across every
  // useLiveBookingQueue poll, a card that ever settled into 'failed'/'stalled'
  // had no path back to 'idle'/'processing' except its own internal handlers
  // (handleClick, handleRetry, the 3s poll below) actually completing. If a
  // retry POST succeeds but this component doesn't hear about it directly —
  // a race, a thrown-then-swallowed error in handleRetry, a second tab, or
  // simply this card's own setClaimState call getting lost — the card was
  // stuck showing "Processing failed" forever even after the backend moved
  // on. This effect makes the fresh polled job.backendStatus the ultimate
  // source of truth, closing that gap within one 30s queue poll regardless
  // of which internal path (or lack thereof) got us here.
  const lastSyncedStatus = useRef(job.backendStatus);

  // Latest values for callbacks that outlive the render they were created in
  // (the 3s claim poll and the status-sync effect below).
  const claimStateRef = useRef(claimState);
  claimStateRef.current = claimState;
  const jobRef = useRef(job);
  jobRef.current = job;
  const onClaimCompleteRef = useRef(onClaimComplete);
  onClaimCompleteRef.current = onClaimComplete;
  // Guards against opening the booking twice when both the 3s poll and the
  // status-sync effect notice the same PENDING_APPROVAL transition.
  const claimCompletedRef = useRef(false);

  async function completeClaim(rawEmailId: string | undefined) {
    if (claimCompletedRef.current) return;
    claimCompletedRef.current = true;
    if (intervalRef.current !== null) clearInterval(intervalRef.current);
    const enriched = await _enrichJob(jobRef.current, rawEmailId);
    onClaimCompleteRef.current(enriched);
  }

  useEffect(() => {
    if (job.backendStatus === lastSyncedStatus.current) return;
    lastSyncedStatus.current = job.backendStatus;

    // Race fix, 22 Oct 2026: useLiveBookingQueue's 30s poll can see this
    // card's workflow reach PENDING_APPROVAL before the card's own 3s claim
    // poll does. The branch below then reset the card to 'idle', which tears
    // down the claim poll before it ever fired onClaimComplete — so the card
    // flipped to PENDING REVIEW but the 3-column view never opened and the
    // operator had to click it again. If this operator started the claim
    // (claimState 'processing'), finish it from here too instead of dropping it.
    if (job.backendStatus === 'PENDING_APPROVAL' && claimStateRef.current === 'processing') {
      getWorkflow(job.id)
        .then((workflow) =>
          completeClaim((workflow as unknown as Record<string, unknown>).raw_email_id as string | undefined),
        )
        .catch(() => {
          claimCompletedRef.current = false;
        });
    }

    if (job.backendStatus === 'FAILED') {
      setClaimState('failed');
    } else if (job.backendStatus === 'PROCESSING') {
      setClaimState((prev) => (prev === 'processing' ? prev : 'processing'));
    } else {
      // RECEIVED goes back to the idle (claimable) card. Every status past the
      // inbound stage (DRAFTED, PENDING_APPROVAL, APPROVED, RESERVATION_CONFIRMED,
      // ...) also resets to 'idle': InboxQueue keeps those cards on screen
      // (as PENDING REVIEW / APPROVED), so the card is NOT unmounted — without
      // this, a card that was showing the "AI extraction in progress" spinner
      // stayed stuck on it until a hard reload whenever the card's own 3s poll
      // missed the transition.
      setClaimState('idle');
    }
    // Keyed on the backend status transition only; everything else this
    // reads goes through refs so it is always current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.backendStatus]);

  // Polling effect — runs whenever claimState becomes 'processing'.
  useEffect(() => {
    if (claimState !== 'processing') return;

    let elapsed = 0;
    claimCompletedRef.current = false;

    intervalRef.current = window.setInterval(async () => {
      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= POLL_TIMEOUT_MS) {
        clearInterval(intervalRef.current!);
        setClaimState('timeout');
        return;
      }
      try {
        const workflow = await getWorkflow(job.id);
        const wfStatus = (workflow as Record<string, unknown>).status as string | undefined;
        if (wfStatus === 'PENDING_APPROVAL') {
          clearInterval(intervalRef.current!);
          const rawEmailId = (workflow as Record<string, unknown>).raw_email_id as string | undefined;
          await completeClaim(rawEmailId);
        } else if (wfStatus === 'FAILED') {
          clearInterval(intervalRef.current!);
          setClaimState('failed');
        }
      } catch {
        // transient fetch error — keep polling
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
    // `job`/`onClaimComplete` deliberately excluded — this effect is meant to
    // start exactly once when claimState becomes 'processing' and keep its
    // own `elapsed` counter running via the closure over `job`/onClaimComplete
    // at that moment. useLiveBookingQueue rebuilds `job` with a new object
    // identity on every 30s poll regardless of whether this specific booking
    // actually changed — depending on it here would tear down and restart
    // the interval (resetting `elapsed` to 0) on every one of those polls,
    // so POLL_TIMEOUT_MS would never actually be reached.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimState]);

  async function handleClick() {
    if (claimState !== 'idle') return;
    // Only RECEIVED cards are clickable — PROCESSING cards show spinner and are inert.
    if (job.backendStatus === 'PROCESSING') return;
    // Cards past PENDING_APPROVAL reopen the 3-column view instead of
    // re-claiming from scratch — but re-entry must still go through
    // claim_workflow() rather than calling onSelect() directly. It used to
    // skip straight to onSelect(), which meant the backend's 409
    // claim-lock only ever fired on the very first RECEIVED->PROCESSING
    // claim: two operators could both open the same PENDING_APPROVAL/
    // APPROVED/etc. booking with no conflict warning. claim_workflow()
    // already grants plain read access (200, no reassignment) for any
    // status beyond RECEIVED as long as it isn't claimed by someone else —
    // this reuses that existing branch instead of bypassing it.
    // See [[finding-claim-locking-frontend-ui]] ("Not yet done").
    if (
      job.backendStatus === 'DRAFTED' ||
      job.backendStatus === 'PENDING_APPROVAL' ||
      job.backendStatus === 'APPROVED' ||
      job.backendStatus === 'RESERVATION_CONFIRMED'
    ) {
      try {
        await claimWorkflow(job.id);
        onSelect(job);
      } catch (err) {
        const message =
          err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : '';
        if (message.toLowerCase().includes('already claimed')) {
          toast.warning(message);
          return;
        }
        // Any other error (network blip, etc.) — don't block the operator
        // on a transient failure; fall back to opening the card.
        onSelect(job);
      }
      return;
    }
    try {
      await claimWorkflow(job.id);
      claimStartedAtRef.current = Date.now();
      setClaimState('processing');
    } catch (err) {
      // 409 means someone else claimed it between this card rendering and
      // the click landing — client.ts now surfaces the backend's real
      // "Workflow already claimed by X" detail instead of a bare status
      // code, so show the operator who actually has it instead of just
      // failing silently into the generic retry state.
      const message =
        err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : '';
      if (message.toLowerCase().includes('already claimed')) {
        toast.warning(message);
        setClaimState('idle');
        return;
      }
      setClaimState('failed');
    }
  }

  // ── Spinner card (PROCESSING state) ──────────────────────────────────────
  if (claimState === 'processing') {
    return (
      <div
        style={{
          background: brand.surface2,
          border: `1px solid ${brand.hairline}`,
          borderLeft: `3px solid ${brand.accent}`,
          borderRadius: 10,
          padding: '16px 20px',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: fonts.sans, fontSize: 14, fontWeight: 600, color: brand.onSurface }}>
              {job.clientName === 'Loading...' ? 'Processing…' : job.clientName}
            </span>
          </div>
          <ExtractionProgress variant="compact" startedAt={claimStartedAtRef.current} />
          {job.assignedToEmail && job.assignedToEmail !== user?.email && (
            <div style={{ fontFamily: fonts.mono, fontSize: 10, color: brand.monoDim, marginTop: 4 }}>
              CLAIMED BY {job.assignedToEmail.toUpperCase()}
            </div>
          )}
        </div>
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 20, color: brand.accent, animation: 'spin 1s linear infinite' }}
        >
          autorenew
        </span>
      </div>
    );
  }

  // ── Stalled card (created_at > 3 min and still PROCESSING) ──────────────
  if (claimState === 'stalled' || claimState === 'timeout' || claimState === 'failed') {
    const message =
      claimState === 'failed'
        ? 'Processing failed — retry or dismiss'
        : 'Extraction stalled — click to retry or dismiss';

    async function handleRetry() {
      try {
        await retryWorkflow(job.id);
        setClaimState('idle');
      } catch {
        // stay in failed state if retry endpoint fails
      }
    }

    // Dismiss used to only ever hide the card locally (InboxQueue's own
    // `dismissed` Set) with no backend call at all — so a dismissed card
    // reappeared on the next 30s poll or any reload, since the workflow
    // was still genuinely RECEIVED/PROCESSING/FAILED server-side the
    // whole time. Reported live 07 Sep 2026. Now calls the real
    // /dismiss endpoint (FAILED, or stalled PROCESSING, -> QUARANTINED)
    // first, and only tells the parent to hide the card once the backend
    // has actually confirmed it — a failed call surfaces a toast and
    // leaves the card in place so the operator can retry, instead of
    // silently pretending it worked.
    async function handleDismissClick() {
      try {
        await dismissWorkflow(job.id);
        onDismiss();
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Failed to dismiss booking';
        toast.error(message);
      }
    }

    return (
      <div
        style={{
          background: brand.surface2,
          border: `1px solid ${brand.errorBorderMid}`,
          borderLeft: '3px solid rgb(239,68,68)',
          borderRadius: 10,
          padding: '16px 20px',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: fonts.sans, fontSize: 14, fontWeight: 600, color: brand.onSurface }}>
            {job.clientName === 'Loading...' ? 'Unknown sender' : job.clientName}
          </div>
          <div style={{ fontFamily: fonts.mono, fontSize: 11, color: 'rgb(239,68,68)', marginTop: 4 }}>
            {message}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={handleRetry}
            style={{
              background: brand.errorBg,
              border: `1px solid ${brand.errorBorderMid}`,
              borderRadius: 6,
              color: 'rgb(239,68,68)',
              cursor: 'pointer',
              fontFamily: fonts.mono,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              padding: '4px 10px',
              whiteSpace: 'nowrap',
            }}
          >
            Retry
          </button>
          <button
            onClick={handleDismissClick}
            style={{
              background: 'transparent',
              border: `1px solid ${brand.hairlineMid}`,
              borderRadius: 6,
              color: brand.monoDim,
              cursor: 'pointer',
              fontFamily: fonts.mono,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              padding: '4px 10px',
              whiteSpace: 'nowrap',
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // ── Normal RECEIVED card ─────────────────────────────────────────────────
  const preview =
    job.emailBody.length > 140 ? job.emailBody.slice(0, 140) + '...' : job.emailBody;
  const inboxBadge = getInboxBadge(job.backendStatus);
  const inboxTone = statusColors(inboxBadge.label);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      style={{
        background: hovered ? brand.hairlineSoft : brand.surface2,
        border: `1px solid ${brand.hairline}`,
        borderLeft: `3px solid ${brand.accent}`,
        borderRadius: 10,
        padding: '16px 20px',
        cursor: 'pointer',
        display: 'flex',
        gap: 12,
        transition: `background ${motion.default}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontFamily: fonts.sans,
              fontSize: 14,
              fontWeight: 600,
              color: brand.onSurface,
            }}
          >
            {job.clientName}
          </span>
          <span style={{ ...styles.monoLabel, color: brand.monoDim }}>
            {job.receivedAt ? formatSASTShort(job.receivedAt) : ''}
          </span>
        </div>
        <div
          style={{
            fontFamily: fonts.mono,
            fontSize: 11,
            color: brand.monoDim,
            marginTop: 2,
          }}
        >
          {job.clientEmail}
        </div>
        <div
          style={{
            fontFamily: fonts.sans,
            fontSize: 13,
            color: brand.onSurface,
            marginTop: 10,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {job.subject}
        </div>
        <div
          style={
            {
              fontFamily: fonts.sans,
              fontSize: 12,
              lineHeight: 1.6,
              color: brand.onSurfaceVariant,
              marginTop: 4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            } as CSSProperties
          }
        >
          {preview}
        </div>
      </div>

      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontFamily: fonts.mono,
            fontSize: 9,
            textTransform: 'uppercase',
            color: inboxTone.color,
            background: inboxTone.background,
            border: `1px solid ${inboxTone.border}`,
            padding: '2px 8px',
            borderRadius: 4,
          }}
        >
          <span
            aria-hidden
            style={{ width: 5, height: 5, borderRadius: 999, background: inboxTone.color, flexShrink: 0 }}
          />
          {inboxBadge.label}
        </span>
        {job.isThreadReply && (
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: 9,
              textTransform: 'uppercase',
              color: brand.monoDim,
            }}
          >
            THREAD REPLY
          </span>
        )}
        {job.assignedToEmail && job.assignedToEmail !== user?.email && (
          <span
            title={`Claimed by ${job.assignedToEmail}`}
            style={{
              fontFamily: fonts.mono,
              fontSize: 9,
              textTransform: 'uppercase',
              color: brand.monoDim,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 120,
            }}
          >
            CLAIMED: {job.assignedToEmail}
          </span>
        )}
        <span
          className="material-symbols-outlined"
          style={{
            fontSize: 18,
            color: brand.accent,
            opacity: hovered ? 1 : 0,
            transition: `opacity ${motion.default}`,
          }}
        >
          chevron_right
        </span>
      </div>
    </div>
  );
}

function TerminalPlaceholder({ label }: { label: string }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        color: brand.monoDim,
        fontFamily: 'var(--font-jetbrains-mono)',
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
  );
}

